import React from 'react';
import Card from '../components/Card';
import Carousel from '../components/Carousel';
import { useData } from '../context/DataContext';
import TagsHoc from '../components/hoc/TagsHoc';
import { useFavorites } from '../context/FavoritesContext';
import { RouteComponentProps } from 'react-router';
import Popup from '../components/Popup';
import { useQueryStrings } from '../context/QueryStringContext';
import {
  DailyProgramVenue,
  DailyProgramEvent,
} from '../../../../../../../shared/types/dailyProgram';
import OtherTimes from '../components/OtherTimes';
import SimilarTimesHoc from '../components/hoc/SimilarTimesHoc';
import { intersection } from '../../../../../../../shared/lib/functional';
import { useApp } from '../context/AppContext';
import { getOptimizedEdpImageUrl } from '../../../../../../../shared/lib/image';

interface LineupDetailsPagePropsBase {
  event?: DailyProgramEvent;
}

type LineupDetailsPageProps = LineupDetailsPagePropsBase & RouteComponentProps;

export default function LineupDetailsPage(props: LineupDetailsPageProps) {
  const { dateLineup } = useData();
  const { favorites, toggleFavorite } = useFavorites();
  const { updateQs } = useQueryStrings();
  const { config } = useApp();

  function handleVenueClick(venue: DailyProgramVenue) {
    updateQs('venue', venue.templateId);
  }

  function handleEventClick(event: DailyProgramEvent) {
    updateQs('event', event.id);
  }

  function getSimilarEvents(event: DailyProgramEvent) {
    return dateLineup.events.filter(
      e =>
        intersection(e.attributeIds, event.attributeIds).length > 0 &&
        e.templateId != event.templateId
    );
  }

  const { event } = props;
  const venue = dateLineup.venues.find(venue => venue.templateId === event?.venueId);

  const showSimilarEvents =
    event && getSimilarEvents(event).length > 0 && !config.hideSimilarEvents;

  return (
    <Popup onClose={() => updateQs('event', undefined)}>
      {event && (
        <div className="border-b border-slate-300">
          <TagsHoc type="event" item={event} key={event.id}>
            {props => (
              <Card
                detailed
                title={event.title}
                subtitle={[event.venueString, event.timeString]}
                description={event.description}
                image={getOptimizedEdpImageUrl(
                  event.images,
                  event.imageOptimizations,
                  config.imgRequirements,
                  0
                )}
                tags={props.tags}
                isFavorite={!!favorites.events[event.id]}
                onFavoriteClick={() => toggleFavorite({ type: 'events', value: event })}
                callToActions={event.callToActions}
              />
            )}
          </TagsHoc>
          <hr className="border-slate-300 divider" />
          <div className="py-4 bg-white similar-container">
            <SimilarTimesHoc type="events" item={event}>
              {({ similarTimes }) => <OtherTimes similarTimes={similarTimes} />}
            </SimilarTimesHoc>
          </div>
          {showSimilarEvents && (
            <Carousel
              title="Similar Events"
              icon={['far', 'calendar-day']}
              className="carousel-container py-4 border-t border-slate-300"
            >
              {getSimilarEvents(event).map(evt => (
                <TagsHoc type="event" item={evt} key={evt.id}>
                  {props => (
                    <Card
                      key={evt.id}
                      className="carousel border"
                      onClick={() => handleEventClick(evt)}
                      title={evt.title}
                      subtitle={[evt.venueString, evt.timeString]}
                      image={getOptimizedEdpImageUrl(
                        evt.images,
                        evt.imageOptimizations,
                        config.imgRequirements,
                        0
                      )}
                      tags={props.tags}
                      isFavorite={!!favorites.events[evt.id]}
                      onFavoriteClick={() => toggleFavorite({ type: 'events', value: evt })}
                    />
                  )}
                </TagsHoc>
              ))}
            </Carousel>
          )}
          {venue && config.enabledViews.includes('explore') && (
            <TagsHoc type="venue" item={venue} key={venue.templateId}>
              {props => (
                <Card
                  onClick={() => handleVenueClick(venue)}
                  title={venue.venueString}
                  subtitle={venue.timeStrings}
                  image={getOptimizedEdpImageUrl(
                    venue.images,
                    venue.imageOptimizations,
                    config.imgRequirements,
                    0
                  )}
                  tags={props.tags}
                />
              )}
            </TagsHoc>
          )}
        </div>
      )}
    </Popup>
  );
}
